import memoize from 'moize'
import ms from 'ms'

import arriereBackoffice, {
  ComparisonOperator,
  IDate,
  OnBoardingStatus,
  RatingHomeFiltersInput,
  RatingSorting,
  ReviewPeriodKind,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { memoizeIndexedDB } from '@new/__global/components/FastTapeChart/__utils'
import { formatRatingData } from '@new/domains/dashboard/stores/_utils'
import { MarketReviewsParams } from '@new/domains/ratings/marketReviews/MarketReviews.types'

interface RatingFilter {
  column: RatingSorting
  operator: ComparisonOperator
  value: number
}

interface RegionsQueryFilters {
  fromDate: string | IDate
  toDate: string | IDate
  onBoardingStatus?: OnBoardingStatus[]
  page: number
  size: number
  regionIds?: string[]
  filters?: RatingFilter[]
  alias?: string
}

export default {
  getRegions: memoizeIndexedDB(
    'getRegions',
    async () => {
      return arriereBackoffice.queries.regions({
        __alias: 'getRegions',
        __args: { pagination: { page: 1, size: 400 } },
        total: true,
        results: {
          id: true,
          name: true,
          homeCount: true,
        },
      })
    },
    ms('6 hours'),
  ),
}

export const getPropertiesFromRegions = memoize(
  async (regionIds?: string[]) => {
    return arriereBackoffice.queries.homes
      .raw({
        __alias: 'getPropertiesFromRegions',
        __args: {
          regionIds,
          pagination: { page: 1, size: 9000 },
        },
        results: {
          id: true,
          name: true,
          title: true,
          region: {
            name: true,
          },
        },
      })
      .then(({ data, errors }) => {
        if (errors?.length) {
          throw new Error(errors[errors.length - 1]?.message)
        }

        return data
      })
  },
  { maxAge: ms('24 hours'), isPromise: true },
)

export const airbnbGlobalRatingsMetricsFragment = {
  avgAccuracy: true,
  avgCheckIn: true,
  avgCleanliness: true,
  avgCommunication: true,
  avgLocation: true,
  avgOverall: true,
  avgValue: true,
  conversion: {
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
  numberOfReviews: true,
}

export const vrboGlobalRatingsMetricsFragment = {
  avgOverall: true,
  avgCheckIn: true,
  avgCommunication: true,
  avgLocation: true,
  avgOnlineListing: true,
  avgRoomCleanliness: true,
  conversion: {
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
  numberOfReviews: true,
}

export const bookingComGlobalRatingsMetricsFragment = {
  avgCleanliness: true,
  avgLocation: true,
  avgOverall: true,
  avgPropertyCondition: true,
  avgStaff: true,
  avgValue: true,
  conversion: {
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
  numberOfReviews: true,
}

export const expediaGlobalRatingsMetricsFragment = {
  avgAmenities: true,
  avgCleanliness: true,
  avgCommunication: true,
  avgEcoFriendliness: true,
  avgOverall: true,
  avgPropertyCondition: true,
  conversion: {
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
  numberOfReviews: true,
}

export const npsGlobalRatingsMetricsFragment = {
  score: true,
  conversion: {
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
  numberOfReviews: true,
}

export const getAllMarketRatings = async ({
  alias,
  fromDate,
  toDate,
  regionIds,
}: RegionsQueryFilters) => {
  return arriereBackoffice.queries.rating
    .raw({
      __alias: alias ?? 'getAllMarketRatings',
      ratingMetricGlobal: {
        __args: { from: fromDate, to: toDate, regionIds },
        airbnb: {
          ...airbnbGlobalRatingsMetricsFragment,
        },
        nps: {
          ...npsGlobalRatingsMetricsFragment,
        },
        vrbo: {
          ...vrboGlobalRatingsMetricsFragment,
        },
        bookingCom: {
          ...bookingComGlobalRatingsMetricsFragment,
        },
        expedia: {
          ...expediaGlobalRatingsMetricsFragment,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      if (!data.ratingMetricGlobal) throw new Error('Global ratings are unavailable')

      return data.ratingMetricGlobal
    })
}

interface GetMarketRatingsProps extends RegionsQueryFilters {
  homeFilters?: RatingHomeFiltersInput
}

export const getMarketRatings = async ({
  fromDate,
  toDate,
  page,
  size,
  alias,
  ...otherArgs
}: GetMarketRatingsProps & {}) => {
  return arriereBackoffice.queries.rating
    .raw({
      __alias: `${alias || 'getMarketRatings'}`,
      ratingMetricByRegion: {
        __args: {
          from: fromDate,
          to: toDate,
          pagination: { page, size },
          ...otherArgs,
        },
        pages: true,
        total: true,
        results: {
          airbnb: {
            ...airbnbGlobalRatingsMetricsFragment,
          },
          homeCount: true,
          region: {
            active: true,
            id: true,
            name: true,
          },
          nps: {
            ...npsGlobalRatingsMetricsFragment,
          },
          vrbo: {
            ...vrboGlobalRatingsMetricsFragment,
          },
          bookingCom: {
            ...bookingComGlobalRatingsMetricsFragment,
          },
          expedia: {
            ...expediaGlobalRatingsMetricsFragment,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data
    })
}

export const ratingsOverviewFragment = {
  homeAway: {
    overall: {
      avgStars: true,
      totalRates: true,
    },
    onlineListing: {
      avgStars: true,
    },
    checkIn: {
      avgStars: true,
    },
    communication: {
      avgStars: true,
    },
    location: {
      avgStars: true,
    },
    roomCleanliness: {
      avgStars: true,
    },
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
  airBnb: {
    overall: {
      avgStars: true,
      totalRates: true,
    },
    accuracy: {
      avgStars: true,
    },
    checkIn: {
      avgStars: true,
    },
    communication: {
      avgStars: true,
    },
    cleanliness: {
      avgStars: true,
    },
    location: {
      avgStars: true,
    },
    value: {
      avgStars: true,
    },
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
  bookingCom: {
    cleanliness: {
      avgStars: true,
    },
    location: {
      avgStars: true,
    },
    overall: {
      avgStars: true,
    },
    propertyCondition: {
      avgStars: true,
    },
    value: {
      avgStars: true,
    },
    staff: {
      avgStars: true,
    },
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
  expedia: {
    amenities: {
      avgStars: true,
    },
    cleanliness: {
      avgStars: true,
    },
    communication: {
      avgStars: true,
    },
    ecoFriendliness: {
      avgStars: true,
    },
    overall: {
      avgStars: true,
    },
    propertyCondition: {
      avgStars: true,
    },
    conversionRate: true,
    numberOfBookings: true,
    numberOfReviews: true,
  },
}

export const npsRatingsOverviewFragment = {
  count: true,
  conversionRate: true,
  numberOfBookings: true,
  score: true,
  promoters: true,
  detractors: true,
}

export const getMarketNpsScore = async (regionId: string, from: string, to: string) => {
  return arriereBackoffice.queries.region
    .raw({
      __alias: 'getMarketNpsScore',
      __args: {
        regionId,
      },
      indicators: {
        customerExperience: {
          npsInfo: {
            __args: { from, to },
            score: true,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data
    })
}

export const getMarketReviewsOverview = async (regionId: string, from: string, to: string) => {
  return arriereBackoffice.queries.rating
    .raw({
      __alias: 'getMarketReviewsOverview',
      ratingMetricByRegion: {
        __args: {
          from,
          to,
          regionIds: [regionId],
        },
        pages: true,
        total: true,
        results: {
          airbnb: {
            ...airbnbGlobalRatingsMetricsFragment,
          },
          nps: {
            ...npsGlobalRatingsMetricsFragment,
            detractors: true,
            promoters: true,
          },
          vrbo: {
            ...vrboGlobalRatingsMetricsFragment,
          },
          bookingCom: {
            ...bookingComGlobalRatingsMetricsFragment,
          },
          expedia: {
            ...expediaGlobalRatingsMetricsFragment,
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      if (!data.ratingMetricByRegion) throw new Error('Market ratings are unavailable')

      const result = data.ratingMetricByRegion.results[0]

      return {
        indicators: {
          customerExperience: {
            npsInfo: {
              count: result.nps.numberOfReviews,
              conversionRate: result.nps.conversion.conversionRate,
              numberOfBookings: result.nps.conversion.numberOfBookings,
              score: result.nps.score,
              promoters: result.nps.promoters,
              detractors: result.nps.detractors,
            },
          },
        },
        ...formatRatingData(result),
      }
    })
}

export const getMarketReviews = async ({
  homeIds,
  marketId,
  page,
  pageSize,
  source,
  from,
  to,
  terms,
  minRating,
  maxRating,
  categories,
}: MarketReviewsParams) => {
  return arriereBackoffice.queries.region
    .raw({
      __alias: 'getMarketReviews',
      __args: { regionId: marketId },
      name: true,
      homeCount: true,
      reviewsByBookingSources: {
        __args: {
          pagination: { page, size: pageSize },
          periodKind: ReviewPeriodKind.reviewCreationDate,
          period: {
            start: from,
            end: to,
          },
          sources: source,
          terms,
          homeIds,
          minRating,
          maxRating,
          categories,
        },
        total: true,
        pages: true,
        results: {
          bookingId: true,
          bookingHash: true,
          source: true,
          guestName: true,
          period: {
            start: true,
            end: true,
          },
          reviews: {
            id: true,
            source: true,
            privateFeedback: true,
            response: true,
            externalReviewId: true,
            createdAt: true,
            booking: {
              guestId: true,
              guestName: true,
              hash: true,
              home: { name: true },
              guestRate: {
                id: true,
                categories: {
                  category: true,
                  comment: true,
                  rating: true,
                  tags: true,
                },
                createdAt: true,
                privateFeedback: true,
                publicReview: true,
                response: true,
              },
            },
            bookingPeriod: {
              start: true,
              end: true,
            },
            categoryReviews: {
              category: true,
              rating: true,
              comment: true,
              tags: true,
            },
            home: {
              name: true,
            },
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data
    })
}

export const getMarketReviewsSourceFacet = async ({
  homeIds,
  marketId,
  page,
  pageSize,
  from,
  to,
  terms,
  minRating,
  maxRating,
  categories,
}: MarketReviewsParams) => {
  return arriereBackoffice.queries.region
    .raw({
      __alias: 'getReviewsSourceFacet',
      __args: { regionId: marketId },
      reviewsByBookingSources: {
        __args: {
          pagination: { page, size: pageSize },
          periodKind: ReviewPeriodKind.reviewCreationDate,
          period: {
            start: from,
            end: to,
          },
          sources: [],
          terms,
          homeIds,
          minRating,
          maxRating,
          categories,
        },
        sourceFacet: { source: true, count: true },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data
    })
}
